import { api } from './api';

/**
 * Care checks (35c layers 3+4, client orchestration) — runs AFTER a build finishes, in the
 * background, while the learner keeps working.
 *
 * Why this lives on the page and not in a web/service worker: every step talks to the sandboxed
 * preview iframe over postMessage, and only the page can do that. The AI work itself runs
 * server-side, so nothing heavy blocks the UI — this file only coordinates: ask the app to play
 * itself, ask it for screenshots, send both to the reviewer, surface the outcome. Cancellation is a
 * plain AbortSignal checked between steps.
 */

export type CarePhase = 'playtest' | 'snapshot' | 'review';

export interface CareResult {
  ok: boolean;
  summary: string;
  jobQueued: boolean;
  cancelled?: boolean;
}

interface PlaytestSignals {
  errorsDuringPlay?: number;
  distinctFrames?: number;
  [key: string]: unknown;
}

const findPreviewWindow = (): Window | null =>
  (document.querySelector('iframe[title="App preview"]') as HTMLIFrameElement | null)?.contentWindow ?? null;

/** postMessage request/response against the injected QA driver, with timeout + abort. */
function askFrame<T>(
  type: string,
  replyType: string,
  payload: unknown,
  timeoutMs: number,
  signal: AbortSignal,
): Promise<T | null> {
  return new Promise(resolve => {
    const target = findPreviewWindow();
    if (!target || signal.aborted) return resolve(null);
    const id = `qa-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      signal.removeEventListener('abort', onAbort);
      window.clearTimeout(timer);
    };
    const onAbort = () => {
      cleanup();
      resolve(null);
    };
    const timer = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { v?: string; type?: string; id?: string; payload?: T };
      if (data?.v !== 'cbx:v1' || data.type !== replyType || data.id !== id) return;
      cleanup();
      resolve(data.payload ?? null);
    };
    signal.addEventListener('abort', onAbort);
    window.addEventListener('message', onMessage);
    target.postMessage({ v: 'cbx:v1', type, id, payload }, '*');
  });
}

/** Downscale + JPEG-compress a data URL so two shots stay well under the API body limit. */
function shrink(dataUrl: string, maxWidth = 900): Promise<string | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(1, maxWidth / (img.width || maxWidth));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = '#ffffff'; // JPEG has no alpha — transparent snapshots must not go black
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/jpeg', 0.8));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

export async function runCareChecks(input: {
  projectId: string;
  isGame: boolean;
  signal: AbortSignal;
  onPhase: (phase: CarePhase) => void;
}): Promise<CareResult> {
  const { projectId, isGame, signal, onPhase } = input;
  const cancelled = (): CareResult => ({ ok: true, summary: 'Checks stopped.', jobQueued: false, cancelled: true });

  // 1. first look — how the app presents itself before anyone touches it
  onPhase('snapshot');
  const before = await askFrame<{ dataUrl?: string | null }>('snapshot-page-request', 'snapshot-page-result', {}, 6000, signal);
  if (signal.aborted) return cancelled();

  // 2. short self-play (games only) — signals like "the picture never changed" beat any screenshot
  let playtest: PlaytestSignals | null = null;
  if (isGame) {
    onPhase('playtest');
    playtest = await askFrame<PlaytestSignals>('qa-playtest-request', 'qa-playtest-result', { seconds: 6 }, 12000, signal);
    if (signal.aborted) return cancelled();
  }

  // 3. second look — whatever state the playtest left the app in
  onPhase('snapshot');
  const after = isGame
    ? await askFrame<{ dataUrl?: string | null }>('snapshot-page-request', 'snapshot-page-result', {}, 6000, signal)
    : null;
  if (signal.aborted) return cancelled();

  const shots: Array<{ label: string; dataUrl: string }> = [];
  for (const [label, shot] of [
    ['opening screen', before],
    ['after playing for a few seconds', after],
  ] as const) {
    if (!shot?.dataUrl) continue;
    const small = await shrink(shot.dataUrl);
    if (small) shots.push({ label, dataUrl: small });
  }
  if (signal.aborted) return cancelled();
  if (shots.length === 0 && !playtest) {
    return { ok: true, summary: 'Could not take pictures this time — skipped.', jobQueued: false };
  }

  // 4. the reviewer (server-side vision model) decides if anything is actually broken
  onPhase('review');
  try {
    const verdict = await api.post<{ looksRight: boolean; defects: Array<{ severity: string; what: string }>; jobQueued: boolean }>(
      `/box/projects/${projectId}/qa/visual`,
      { shots, playtest: playtest ?? undefined },
    );
    if (signal.aborted) return cancelled();
    if (verdict.jobQueued) {
      return { ok: false, summary: 'Found something that looks broken — fixing it for free now.', jobQueued: true };
    }
    if (!verdict.looksRight && verdict.defects.length > 0) {
      return { ok: false, summary: `Spotted a rough edge: ${verdict.defects[0].what}`, jobQueued: false };
    }
    return { ok: true, summary: 'Everything looks right.', jobQueued: false };
  } catch {
    // the check failing is never the learner's problem
    return { ok: true, summary: 'Checks could not finish this time.', jobQueued: false };
  }
}
